import { AddSeries } from '../../../add-series';
import useAddSeries from '../../../add-series/hooks/use-add-series';
import { useSeriesManager } from './hooks/use-series-manager';

import './styles.scss';
import { useContext, useEffect, useMemo, useState } from 'react';
import Input from '../../../../input/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { RequestTypeEnum, TimeseriesContext } from '../../../../../providers/timeseries-provider';
import classNames from 'classnames';
import CircularProgress from '@mui/material/CircularProgress';

interface AddSurfaceSeriesProps {
  seriesId?: string;
  listTokenFrequency?: any[];
  onApply: (options: Record<string, any>) => void;
  seriesExists: (token: string, tenor: string, delta: string, type?: RequestTypeEnum) => boolean;
  onlyPerps?: boolean;
}

const AddSurfaceSeries = (props: AddSurfaceSeriesProps) => {
  const { onApply, seriesExists, onlyPerps } = props;

  const { isOpen, openModal, closeModal, addSeriesButtonRef } = useAddSeries();

  const { perpsList, fetchPerpsList } = useContext(TimeseriesContext);

  const { options, type, setType, typeOptions, loading } = useSeriesManager({
    perpsList,
    fetchPerpsList,
  });

  const [tokenSearch, setTokenSearch] = useState('');

  const handleSeriesAdded = () => {
    const selectedValues = options.reduce(
      (acc, option) => {
        acc[option.key] = option.value;
        return acc;
      },
      {} as Record<string, any>,
    );

    onApply({ ...selectedValues, ...{ type } });

    closeModal();
  };

  const renderRadioGroup = (
    key: string,
    title: string,
    options: any[],
    value: any,
    onChange: (value: any) => void,
  ) => {
    return (
      <AddSeries.RadioGroup
        title={title}
        value={value}
        onChange={onChange}
        options={options}
        className={`group-${key}`}
      />
    );
  };

  const filteredTypeOptions = onlyPerps
    ? typeOptions.filter((opt: any) => opt.value === RequestTypeEnum.PERPETUAL)
    : typeOptions;

  const renderMainContent = useMemo(() => {
    if (loading) {
      return (
        <AddSeries.Content classname="main-modal-content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
            }}
          >
            <CircularProgress />
          </div>
        </AddSeries.Content>
      );
    }
    const renderOptions = () => {
      return options.map((option) => (
        <AddSeries.RadioGroup
          key={option.label}
          title={option.label}
          value={option.value}
          onChange={option.setValue}
          options={option.options}
          otherOptions={option.otherOptions}
        />
      ));
    };

    return (
      <AddSeries.Content classname="main-modal-content">
        <AddSeries.Title title="Add Surface Series" />
        {renderRadioGroup('type', 'Type', filteredTypeOptions, type, setType)}
        {renderOptions()}
      </AddSeries.Content>
    );
  }, [type, setType, options, loading, filteredTypeOptions]);

  const renderOtherContent = useMemo(() => {
    const tokenOptions = options.find((option) => option.key === 'token');
    const hasOtherOptions =
      tokenOptions && tokenOptions.otherOptions && tokenOptions.otherOptions.length > 0;
    const isOtherSelected =
      hasOtherOptions &&
      tokenOptions?.otherOptions?.find((option) => option.value === tokenOptions.value);

    if (!hasOtherOptions || !isOtherSelected) {
      return null;
    }

    const filteredOtherOptions = tokenOptions.otherOptions?.filter((option) =>
      option.label.toLowerCase().includes(tokenSearch.toLowerCase()),
    );

    return (
      <AddSeries.Content classname="other-tokens-content">
        <span className="token-selector-label">Select another token</span>
        <Input
          icon={<FontAwesomeIcon icon={faSearch} />}
          placeholder="Search token"
          className="search-input"
          value={tokenSearch}
          onChange={(e) => setTokenSearch(e.target.value)}
        />
        <AddSeries.RadioGroup
          value={tokenOptions.value}
          onChange={tokenOptions.setValue}
          options={filteredOtherOptions || []}
        />
      </AddSeries.Content>
    );
  }, [tokenSearch, options]);

  const isOtherContentVisible = useMemo(() => {
    const tokenOptions = options.find((option) => option.key === 'token');
    const hasOtherOptions =
      tokenOptions && tokenOptions.otherOptions && tokenOptions.otherOptions.length > 0;
    const isOtherSelected =
      hasOtherOptions &&
      tokenOptions?.otherOptions?.find((option) => option.value === tokenOptions.value);

    return hasOtherOptions && isOtherSelected;
  }, [options]);

  const isSeriesAlreadyExists = useMemo(() => {
    const token = options.find((o) => o.key === 'token')?.value;
    const tenor = options.find((o) => o.key === 'tenor')?.value || '';
    const delta = options.find((o) => o.key === 'delta')?.value || '';

    return seriesExists(token, tenor, delta, type);
  }, [options]);

  useEffect(() => {
    if (onlyPerps) {
      setType(RequestTypeEnum.PERPETUAL);
    }
  }, [onlyPerps]);

  return (
    <AddSeries>
      <AddSeries.Button onClick={openModal} label="Add Series" ref={addSeriesButtonRef} />
      <AddSeries.Modal
        isOpen={isOpen}
        setIsOpen={closeModal}
        onClose={closeModal}
        className={classNames('add-surface-series-modal', {
          'with-other': isOtherContentVisible,
        })}
        anchorEl={addSeriesButtonRef.current}
      >
        <AddSeries.Column className="main-modal-column">
          {renderMainContent}
          <AddSeries.ActionButtons
            onCancel={closeModal}
            onApply={handleSeriesAdded}
            disabled={isSeriesAlreadyExists}
          />
        </AddSeries.Column>
        {isOtherContentVisible && (
          <AddSeries.Column className="other-token-column">
            {isOtherContentVisible && renderOtherContent}
          </AddSeries.Column>
        )}
      </AddSeries.Modal>
    </AddSeries>
  );
};

export default AddSurfaceSeries;
